import {
  Dialog,
  makeStyles,
  DialogProps as MUIDialogProps
} from '@material-ui/core';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState
} from 'react';
import { v4 as uuid } from 'uuid';

const useModalStyles = makeStyles(() => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 20,
      overflowX: 'hidden'
    }
  }
}));

interface IDialogProvider {
  children: ReactNode;
}

interface DialogOptions {
  // Prevents user from closing dialog by clicking escape or backdrop
  preventClose?: boolean;
  /**
   * Required property if opening multiple dialogs, id acts as a key and has to be passed
   * to specify which dialog should be closed if not provided all opened dialogs should close
   * default id is 1
   */
  id?: number | string;
  /**
   * Required property if using router modals
   * to change current route on dialog close to previous route
   */
  onClose?: () => void;
  /**
   * Required property if black backdrop is not needed
   */
  hideBackdrop?: boolean;
  /**
   * Required property if keyboard focus needs to be disabled
   */
  disableEnforceFocus?: boolean;

  dialogClasses?: MUIDialogProps['classes'];
}

interface ContextProps {
  openDialog(component: ReactNode, options?: DialogOptions): void;
  closeDialog(id?: DialogOptions['id'], onClose?: () => void): void;
}

const defaultValues: ContextProps = {
  openDialog: () => {},
  closeDialog: () => {}
};

interface DialogProps {
  options: DialogOptions;
  component: ReactNode | null;
  id?: DialogOptions['id'];
  onClose?: () => void;
  /** Mount time — used to ignore the backdrop click of a double-click. */
  openedAt: number;
}

export const DialogContext = createContext<ContextProps>(defaultValues);

const DOUBLE_CLICK_GUARD_MS = 300;

const DialogProvider = ({ children }: IDialogProvider) => {
  const [dialogs, setDialogs] = useState<DialogProps[]>([]);
  const styles = useModalStyles();

  const openDialog = useCallback(
    (node: ReactNode, dialogOptions?: DialogOptions) => {
      setDialogs(prevDialogs => {
        const dialog: DialogProps = {
          component: node,
          options: dialogOptions,
          id: dialogOptions?.id || uuid(),
          onClose: () => dialogOptions?.onClose?.(),
          openedAt: Date.now()
        };
        // An explicit id that is already open (e.g. a double-click on a row)
        // replaces that dialog instead of stacking a duplicate key.
        const index =
          dialogOptions?.id != null
            ? prevDialogs.findIndex(d => d.id === dialogOptions.id)
            : -1;
        if (index === -1) return [...prevDialogs, dialog];
        return prevDialogs.map((d, i) => (i === index ? dialog : d));
      });
    },
    []
  );

  /**
   * Provide id to specify which dialog to close,
   * if not provided all opened dialogs will be closed
   */
  const closeDialog = useCallback(
    (id?: DialogOptions['id'], onClose?: () => void) => {
      // overwrite for mui which automatically injects event object as the fist argument to function passed to onClose
      onClose?.();

      if (id && (typeof id === 'string' || typeof id === 'number')) {
        // remove only specified dialog id if value is provided; a functional
        // update so closures created before later dialogs opened (onClose baked
        // into openDialog content) can't restore a stale list
        return setDialogs(prevDialogs =>
          prevDialogs.filter(dialog => dialog.id !== id)
        );
      }

      // remove all dialogs
      setDialogs([]);
    },
    []
  );

  return (
    <>
      <DialogContext.Provider
        value={{
          openDialog,
          closeDialog
        }}>
        {dialogs.map(dialog => (
          <Dialog
            key={dialog?.id}
            open
            disableEnforceFocus={dialog?.options?.disableEnforceFocus || false}
            className={styles.dialog}
            classes={dialog.options?.dialogClasses}
            onClose={(_event, reason) => {
              if (
                dialog.options?.preventClose &&
                (reason === 'backdropClick' || reason === 'escapeKeyDown')
              )
                return;
              // The second click of a double-click on the element that opened
              // the dialog lands on the backdrop — don't treat it as a dismiss.
              if (
                reason === 'backdropClick' &&
                Date.now() - dialog.openedAt < DOUBLE_CLICK_GUARD_MS
              )
                return;

              closeDialog(dialog.id, dialog.onClose);
            }}
            hideBackdrop={dialog?.options?.hideBackdrop}
            maxWidth={false}>
            {dialog.component}
          </Dialog>
        ))}
        {children}
      </DialogContext.Provider>
    </>
  );
};

export default DialogProvider;

export const useDialog = () => useContext<ContextProps>(DialogContext);
